import { ClientTable } from "../../../components/ui/ClientTable";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ListChecks, Plus, Save } from "lucide-react";
import { AppLayout } from "../../../components/layout";
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from "../../../components/ui/dialog";
import { ContentCard, EmptyState, LoadingState, ui } from "../../../components/ui";
import { cn } from "../../../lib/utils";
import sessionDB from "../../../utils/sessionDB";
import {
  addActivity,
  fetchActivities,
  fetchMyActivities,
  getLead,
  updateActivity,
} from "../../../services/leadManagementService";
import { ACTIVITY_STATUSES, formatDay } from "../leadForm";
import { Field, SelectInput, inputClass } from "../components/LeadForm";
import { ASSIGNMENT_FIELDS } from "../components/AssigningTechnician";
import { LeadPageHeader, LeadTabs, openLeadTab } from "./LeadDetailPage";


const K = "leadManagement.activities.";

const STATUS_TONE = { Not_Started: "archived", In_Progress: "scheduled", On_Hold: "warning", Completed: "published" };

const cell = "px-4 py-3 align-middle";

/** Read-only remarks, greyed like the form's other read-only values; two lines show, longer ones scroll. */
const remarksReadOnly =
  "max-h-[58px] w-full min-w-[180px] max-w-sm overflow-y-auto whitespace-pre-wrap rounded-xl border border-(--color-text-border) bg-gray-50 px-3 py-2 text-[12px] leading-5 per-file-hint [overflow-wrap:anywhere]";

/** The people an activity can be assigned to, as [id, name]: the lead's creator and its assignees. */
const leadPeople = (lead) => [
  [lead.createdBy, lead.createdName],
  ...ASSIGNMENT_FIELDS.map(({ key, name }) => [lead[key], lead[name]]),
];

/** Names by id, from what the lead and its activities carry; nothing is fetched. */
const namesFrom = (lead, rows) =>
  Object.fromEntries(
    [
      ...leadPeople(lead),
      ...rows.flatMap((row) => [[row.createdBy, row.createdName], [row.updatedBy, row.updatedName]]),
    ]
      .filter(([id, name]) => id != null && name)
      .map(([id, name]) => [String(id), name])
  );

/* -------------------------------------------------------------- parts */

function StatusBadge({ status }) {
  const { t } = useTranslation();
  const tone = STATUS_TONE[status] ?? "archived";
  return (
    <span
      className="inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold"
      style={{ backgroundColor: `var(--status-${tone}-bg)`, color: `var(--status-${tone}-fg)` }}
    >
      {t(`${K}status${status}`)}
    </span>
  );
}

function Modal({ title, busy, onCancel, footer, children }) {
  const { t } = useTranslation();
  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onCancel()}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/50 backdrop-blur-xs" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] max-w-[460px] sm:w-[460px]",
            "rounded-2xl border border-border bg-card p-6 font-sans shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)]",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          )}
        >
          <DialogTitle className="mb-4 text-[16px] font-bold text-foreground">{title}</DialogTitle>
          {children}
          <div className="mt-5 flex justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="h-10 rounded-xl border border-primary/30 bg-card px-4 text-sm font-semibold text-primary transition hover:bg-muted disabled:opacity-50"
            >
              {t("formActions.cancel")}
            </button>
            {footer}
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

/** Add Activity (PRD Screen 8): one row appended to the tracker on save. */
function AddActivityDialog({ people, statusOptions, busy, error, onCancel, onSave }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    activityName: "", assignedTo: "", dueDate: "", completedDate: "", activityStatus: "Not_Started", remarks: "",
  });
  const [touched, setTouched] = useState(false);

  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));
  const required = (value) => (touched && !value ? t("leadManagement.errorsRequired") : "");

  const save = () => {
    setTouched(true);
    if (!form.activityName.trim() || !form.assignedTo) return;
    onSave({
      activityName: form.activityName.trim(),
      assignedTo: Number(form.assignedTo),
      dueDate: form.dueDate || null,
      completedDate: form.completedDate || null,
      activityStatus: form.activityStatus,
      remarks: form.remarks.trim() || null,
    });
  };

  return (
    <Modal
      title={t(`${K}add`)}
      busy={busy}
      onCancel={onCancel}
      footer={
        <button type="button" onClick={save} disabled={busy} className={cn(ui.primaryBtn, "h-10 px-4")}>
          {busy ? t("leadManagement.saving") : t(`${K}save`)}
        </button>
      }
    >
      <div className="space-y-4">
        <Field label={t(`${K}name`)} htmlFor="activity-name" required error={required(form.activityName.trim())}>
          <input
            id="activity-name"
            value={form.activityName}
            disabled={busy}
            onChange={(event) => set("activityName")(event.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label={t(`${K}assignedTo`)} htmlFor="activity-assignedTo" required error={required(form.assignedTo)}>
          <SelectInput
            id="activity-assignedTo"
            value={form.assignedTo}
            options={people}
            placeholder={t("leadManagement.placeholdersSelect")}
            disabled={busy}
            error={required(form.assignedTo)}
            onChange={set("assignedTo")}
          />
        </Field>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={t(`${K}dueDate`)} htmlFor="activity-dueDate">
            <input
              id="activity-dueDate"
              type="date"
              min={new Date().toLocaleDateString("en-CA")}
              value={form.dueDate}
              disabled={busy}
              onChange={(event) => set("dueDate")(event.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label={t(`${K}completedDate`)} htmlFor="activity-completedDate">
            <input
              id="activity-completedDate"
              type="date"
              value={form.completedDate}
              disabled={busy}
              onChange={(event) => set("completedDate")(event.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label={t(`${K}status`)} htmlFor="activity-status" className="sm:col-span-2">
            <SelectInput
              id="activity-status"
              value={form.activityStatus}
              options={statusOptions}
              disabled={busy}
              onChange={set("activityStatus")}
            />
          </Field>
        </div>
        <Field label={t(`${K}remarks`)} htmlFor="activity-remarks">
          <textarea
            id="activity-remarks"
            rows={3}
            value={form.remarks}
            disabled={busy}
            onChange={(event) => set("remarks")(event.target.value)}
            className={cn(ui.input, "h-auto w-full py-2")}
          />
        </Field>
      </div>
      {error ? <p className="mt-3 text-[12px] font-semibold text-rose-600">{error}</p> : null}
    </Modal>
  );
}

/* ----------------------------------------------------------- content */

/**
 * Activities Tracker (PRD Screen 5). The backend owns the rules: it filters
 * "My Activities", rejects changes on a locked lead, and lets only an
 * activity's creator or assignee change its status and remarks.
 */
function ActivitiesContent({ lead }) {
  const { t } = useTranslation();
  const { leadId } = lead;
  const me = String(sessionDB.getEmpId());
  const locked = Boolean(lead.isLocked);

  const [rows, setRows] = useState([]);
  const [mine, setMine] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const [dialogError, setDialogError] = useState("");
  // Unsaved status, completed date and remarks, by activity; a row's Save sends them together.
  const [drafts, setDrafts] = useState({});

  // An activity is assigned to someone on the lead: its creator or one of its five assignees.
  const names = namesFrom(lead, rows);
  const people = [...new Set(leadPeople(lead).filter(([id]) => id != null).map(([id]) => String(id)))].map((id) => ({
    value: id,
    label: names[id] ?? id,
  }));
  const statusOptions = ACTIVITY_STATUSES.map((status) => ({ value: status, label: t(`${K}status${status}`) }));

  const load = useCallback(() => {
    setLoading(true);
    return (mine ? fetchMyActivities(leadId) : fetchActivities(leadId))
      .then(setRows)
      .catch((loadError) => setError(loadError.message))
      .finally(() => setLoading(false));
  }, [leadId, mine]);

  useEffect(() => {
    load();
  }, [load]);

  /** Runs a change, then re-reads the list; a dialog shows its own error. */
  const run = async (action, { inDialog = false, done } = {}) => {
    setBusy(true);
    setError("");
    setDialogError("");
    try {
      await action();
      done?.();
      await load();
    } catch (actionError) {
      (inDialog ? setDialogError : setError)(actionError.message);
    } finally {
      setBusy(false);
    }
  };

  const closeDialogs = () => {
    setAdding(false);
    setDialogError("");
  };

  // Status, completed date and remarks are changed only by the activity's creator or its assignee.
  const canEdit = (row) => !locked && [row.createdBy, row.assignedTo].map(String).includes(me);

  const draftOf = (row) => ({
    activityStatus: row.activityStatus,
    completedDate: row.completedDate ?? "",
    remarks: row.remarks ?? "",
    ...drafts[row.activityId],
  });

  const editDraft = (row, field, value) =>
    setDrafts((current) => ({ ...current, [row.activityId]: { ...current[row.activityId], [field]: value } }));

  const isDirty = (row) => {
    const draft = draftOf(row);
    return (
      draft.activityStatus !== row.activityStatus ||
      draft.completedDate !== (row.completedDate ?? "") ||
      draft.remarks.trim() !== (row.remarks ?? "")
    );
  };

  /** Saves the row's status, completed date and remarks in one update; an emptied remarks box clears them. */
  const saveRow = (row) => {
    const { activityStatus, completedDate, remarks } = draftOf(row);
    run(() => updateActivity(row.activityId, { activityStatus, completedDate: completedDate || null, remarks: remarks.trim() }), {
      done: () => setDrafts(({ [row.activityId]: saved, ...rest }) => rest),
    });
  };

  const statusCell = (row) =>
    !canEdit(row) ? (
      <StatusBadge status={row.activityStatus} />
    ) : (
      <div className="w-[150px]">
        <SelectInput
          id={`activity-status-${row.activityId}`}
          value={draftOf(row).activityStatus}
          options={statusOptions}
          disabled={busy}
          onChange={(activityStatus) => editDraft(row, "activityStatus", activityStatus)}
        />
      </div>
    );

  const completedDateCell = (row) =>
    !canEdit(row) ? (
      <span className="per-file-hint">{formatDay(row.completedDate) || "—"}</span>
    ) : (
      <input
        id={`activity-completedDate-${row.activityId}`}
        type="date"
        aria-label={t(`${K}completedDate`)}
        value={draftOf(row).completedDate}
        disabled={busy}
        onChange={(event) => editDraft(row, "completedDate", event.target.value)}
        className={cn(ui.input, "h-9 w-[150px] text-[12px]")}
      />
    );

  // Two lines of remarks; greyed out for those who can't edit.
  const remarksCell = (row) =>
    !canEdit(row) ? (
      <div className={remarksReadOnly}>{row.remarks || "—"}</div>
    ) : (
      <textarea
        id={`activity-remarks-${row.activityId}`}
        aria-label={t(`${K}remarks`)}
        rows={2}
        value={draftOf(row).remarks}
        disabled={busy}
        onChange={(event) => editDraft(row, "remarks", event.target.value)}
        // `!` outranks the form page's own textarea min-height and resize rules.
        className={cn(ui.input, "h-[58px]! min-h-0! w-full min-w-[180px] max-w-sm resize-none! py-2 text-[12px] leading-5")}
      />
    );

  // The row's Save, in its own column; only for those who can edit.
  const saveButton = (row) =>
    canEdit(row) ? (
      <button
        type="button"
        onClick={() => saveRow(row)}
        disabled={busy || !isDirty(row)}
        className={cn(ui.primaryBtn, "h-8 shrink-0 gap-1.5 px-3 text-xs")}
      >
        <Save className="h-3.5 w-3.5" />
        {t(`${K}saveRow`)}
      </button>
    ) : null;

  const assignee = (row) => names[String(row.assignedTo)] ?? row.assignedTo;

  return (
    <div className="space-y-5">
      <ContentCard
        title={t("leadManagement.tabsActivities")}
        bodyClassName="p-0"
        action={
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-pressed={mine}
              onClick={() => setMine((current) => !current)}
              className={cn(ui.filterBtn, mine && "border-primary/40 bg-primary/5 ring-2 ring-primary/15")}
            >
              {t(`${K}assignedToMe`)}
            </button>
            {locked ? null : (
              <button type="button" onClick={() => setAdding(true)} className={ui.filterBtn}>
                <Plus className="h-4 w-4" />
                {t(`${K}add`)}
              </button>
            )}
          </div>
        }
      >
        {loading ? (
          <LoadingState message={t(`${K}loading`)} />
        ) : !rows.length ? (
          <EmptyState icon={ListChecks} title={t(mine ? `${K}emptyMine` : `${K}empty`)} />
        ) : (
          <>
            <div className="hidden overflow-x-auto md:block">
              <ClientTable className="w-full text-sm" style={{ minWidth: "1090px" }}>
                <thead>
                  <tr className={ui.tableHead}>
                    <th className={cn(cell, "font-semibold")}>{t(`${K}name`)}</th>
                    <th className={cn(cell, "font-semibold")}>{t(`${K}assignedTo`)}</th>
                    <th className={cn(cell, "font-semibold")}>{t(`${K}dueDate`)}</th>
                    <th className={cn(cell, "font-semibold")}>{t(`${K}completedDate`)}</th>
                    <th className={cn(cell, "font-semibold")}>{t(`${K}status`)}</th>
                    <th className={cn(cell, "font-semibold")}>{t(`${K}remarks`)}</th>
                    <th className={cell} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-(--color-text-border)/35">
                  {rows.map((row) => (
                    <tr key={row.activityId}>
                      <td className={cn(cell, "font-semibold text-foreground")}>{row.activityName}</td>
                      <td className={cn(cell, "text-foreground")}>{assignee(row)}</td>
                      <td className={cn(cell, "per-file-hint")}>{formatDay(row.dueDate) || "—"}</td>
                      <td className={cell}>{completedDateCell(row)}</td>
                      <td className={cell}>{statusCell(row)}</td>
                      <td className={cell}>{remarksCell(row)}</td>
                      <td className={cn(cell, "text-right")}>{saveButton(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </ClientTable>
            </div>

            <div className="divide-y divide-(--color-text-border)/35 md:hidden">
              {rows.map((row) => (
                <div key={row.activityId} className="space-y-2 px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-foreground">{row.activityName}</p>
                    <p className="text-[11px] per-file-hint">
                      {assignee(row)}
                      {row.dueDate ? ` · ${formatDay(row.dueDate)}` : ""}
                    </p>
                  </div>
                  {statusCell(row)}
                  {row.completedDate || canEdit(row) ? (
                    <div className="flex items-center gap-2 text-[11px] per-file-hint">
                      <span>{t(`${K}completedDate`)}</span>
                      {completedDateCell(row)}
                    </div>
                  ) : null}
                  {row.remarks || canEdit(row) ? remarksCell(row) : null}
                  {canEdit(row) ? <div className="flex justify-end">{saveButton(row)}</div> : null}
                </div>
              ))}
            </div>
          </>
        )}
      </ContentCard>

      {error ? <p className="text-[12px] font-semibold text-rose-600">{error}</p> : null}

      {adding ? (
        <AddActivityDialog
          people={people}
          statusOptions={statusOptions}
          busy={busy}
          error={dialogError}
          onCancel={closeDialogs}
          onSave={(activity) => run(() => addActivity(leadId, activity), { inDialog: true, done: closeDialogs })}
        />
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------- page */

/** Activities has its own URL, so a reload stays on it. */
export function ActivitiesPage() {
  const { t } = useTranslation();
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    getLead(leadId)
      .then(setLead)
      .catch((error) => setLoadError(error.message));
  }, [leadId]);

  return (
    <AppLayout className="space-y-5">
      {lead ? (
        <LeadPageHeader lead={lead} />
      ) : null}
      {loadError ? <p className="text-[12px] font-semibold text-rose-600">{loadError}</p> : null}

      <LeadTabs active="activities" onChange={(tab) => tab !== "activities" && openLeadTab(navigate, leadId, tab, lead)} />

      {lead ? (
        <ActivitiesContent lead={lead} />
      ) : (
        <ContentCard>
          <LoadingState message={t(`${K}loading`)} />
        </ContentCard>
      )}
    </AppLayout>
  );
}

export default ActivitiesPage;
